<template>
  <div>
    <div
      :class="
        selectedArea === 'overview' && selectedDisplayType === 'graph'
          ? 'tw:hidden tw:xl:flex tw:gap-3'
          : 'tw:hidden tw:lg:flex tw:gap-3'
      "
    >
      <BookingTimeframeFilter
        v-if="selectedArea === 'bookings'"
        :id="'rate-connect-performance-time-select'"
        class="tw:w-[14%] tw:min-w-[280px]"
        :options="periodDatesBookingOptions"
        @select="onSelectedBookingPeriodChange"
        name="bookingTimeframe"
        :label="$t('rc_performance_timeframe')"
        :disabled="false"
        :value="selectedPeriodBookingValue"
      />

      <FilterSelect
        v-if="selectedArea === 'overview'"
        :id="'rate-connect-performance-time-select'"
        class="tw:w-[14%] tw:min-w-[200px]"
        :options="periodDatesOptions"
        @select="onSelectedPeriodChange"
        name="timeframe"
        :label="$t('rc_performance_timeframe')"
        :disabled="false"
        :value="selectedPeriodValue"
        :valueLabel="selectedPeriodValue?.label"
      />
      <FilterSelect
        v-if="selectedArea === 'overview'"
        :id="'rate-connect-performance-time-select'"
        class="tw:w-[14%] tw:min-w-[200px]"
        :options="aggregationOptions"
        @select="onSelectedAggregationChange"
        name="aggregation"
        :label="$t('rc_performance_group_data')"
        :disabled="false"
        :value="selectedAggregationValue"
        :valueLabel="selectedAggregationValue?.label"
      />
      <FilterSelect
        v-if="selectedArea === 'overview' && selectedDisplayType === 'graph'"
        :id="'rate-connect-performance-show-graph'"
        class="tw:w-[14%] tw:min-w-[200px]"
        :options="showGraphOptions"
        @select="onSelectedGraphChange"
        name="category"
        :label="$t('rc_performance_category')"
        :disabled="false"
        :value="selectedShowGraphValue"
        :valueLabel="selectedShowGraphValue?.label"
      />
      <MarketsFilter
        v-if="selectedArea === 'overview'"
        id="rate-connect-performance-market-select"
        ariaLabel="rate-connect-performance-market-select"
        size="big"
        class="tw:w-[14%] tw:min-w-[200px]"
        name="markets"
        :label="$t('rc_performance_markets')"
        :options="marketOptions"
        :value="selectedMarketValue"
        :valueLabel="selectedMarketValue?.label"
        @select="onSelectedMarketChange"
      />

      <!-- Web Bookings Flyout   -->
      <div class="tw:relative tw:hidden tw:lg:flex">
        <ModalFilters
          v-if="selectedArea === 'bookings'"
          @openFilters="openBookingFilters"
          @closeFilters="closeBookingFilters"
          @onApply="applyFilters"
          :isModalOpen="bookingFiltersOpened"
          :currentFilters="webCurrentFilters"
          :selectedArea="selectedArea"
          :marketsOptions="marketOptions"
          :selectedMarket="selectedMarketValue"
          :selectedStatus="selectedStatusValue"
          :statusOptions="statusOptions"
          type="booking-flyout"
        />
      </div>
      <div v-if="selectedArea === 'bookings'" class="tw:border-r tw:border-gray-200 tw:h-[44px] tw:w-[1px]" />
      <ColumnsCustomizationField
        v-if="selectedArea === 'bookings'"
        :columns="columns"
        class="tw:w-[14%] tw:min-w-[200px]"
      />
    </div>

    <!-- Mobile Flyout -->
    <div
      :class="
        selectedArea === 'overview' && selectedDisplayType === 'graph'
          ? 'tw:hidden tw:lg:flex tw:xl:hidden'
          : 'tw:hidden tw:landing:flex tw:lg:hidden'
      "
    >
      <ModalFilters
        type="mobile-flyout"
        @openFilters="openFlyoutsFilters"
        @closeFilters="closeFlyoutsFilters"
        @onApply="applyFilters"
        :isModalOpen="filtersFlayoutOpened"
        :currentFilters="modalFlyoutFilters"
        :selectedArea="selectedArea"
        :marketsOptions="marketOptions"
        :selectedMarket="selectedMarketValue"
        :selectedDisplayType="selectedDisplayType"
        :timeframeOptions="selectedArea === 'bookings' ? periodDatesBookingMobileOptions : periodDatesOptions"
        :selectedTimeframe="selectedArea === 'bookings' ? selectedPeriodBookingValue : selectedPeriodValue"
        :aggregationOptions="aggregationOptions"
        :selectedAggregation="selectedAggregationValue"
        :showGraphOptions="showGraphOptions"
        :selectedShowGraphValue="selectedShowGraphValue"
        :dateTypes="dateTypes"
        :selectedDateType="selectedDateTypeValue"
        :selectedStatus="selectedStatusValue"
        :statusOptions="statusOptions"
        :columnsOptions="columns"
      />
    </div>

    <!-- Mobile -->
    <div class="tw:flex tw:landing:hidden">
      <div>
        <GhostButton class="tw:flex tw:items-center tw:gap-2" @click="openMobileFilters">
          <FiltersIcon class="filter tw:fill-gray-800 tw:size-[18px]" />
          {{ $t('filters') }}
          <span
            v-if="appliedFiltersAmount > 0"
            class="filter-badge tw:bg-blue-700 tw:text-white tw:rounded-full tw:w-[20px] tw:h-[20px] tw:text-sm"
          >
            {{ appliedFiltersAmount }}
          </span>
        </GhostButton>
      </div>
      <ModalFiltersMobile
        @closeFilters="closeMobileFilters"
        @onApply="applyFilters"
        :currentFilters="modalFlyoutFilters"
        :selectedArea="selectedArea"
        :selectedDisplayType="selectedDisplayType"
        :isModalOpen="mobileFiltersOpened"
        :timeframeOptions="selectedArea === 'bookings' ? periodDatesBookingMobileOptions : periodDatesOptions"
        :selectedTimeframe="selectedArea === 'bookings' ? selectedPeriodBookingValue : selectedPeriodValue"
        :aggregationOptions="aggregationOptions"
        :selectedAggregation="selectedAggregationValue"
        :marketsOptions="marketOptions"
        :selectedMarket="selectedMarketValue"
        :showGraphOptions="showGraphOptions"
        :selectedShowGraphValue="selectedShowGraphValue"
        :dateTypes="dateTypes"
        :selectedDateType="selectedDateTypeValue"
        :selectedStatus="selectedStatusValue"
        :statusOptions="statusOptions"
        :columnsOptions="columns"
      />
    </div>
  </div>
</template>
<script setup>
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { useRateConnectPerformance } from './useRateConnectPerformance'

import FilterSelect from '@/components/base/filters/FilterSelect.vue'
import MarketsFilter from '@/components/rateConnect/performance/MarketsFilter.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import FiltersIcon from '@/components/icons/FiltersIcon.svg?component'
import ModalFilters from '@/components/rateConnect/performance/modalFilters/ModalFilters.vue'
import ModalFiltersMobile from './modalFilters/ModalFiltersMobile.vue'
import ColumnsCustomizationField from './ColumnsCustomizationField.vue'
import BookingTimeframeFilter from './BookingTimeframeFilter.vue'

const { t } = useI18n()
const props = defineProps({
  markets: Array,
  lastUpdated: String,
  selectedArea: String,
  selectedDisplayType: String,
  overviewTableCampaignType: String,
  columns: Array,
})

const webCurrentFilters = computed(() => {
  const filters = []
  if (props.selectedArea === 'bookings') {
    filters.push({ value: 'status', label: 'rc_booking_status_header' })
    filters.push({ value: 'markets', label: 'rc_performance_markets' })
  }

  return filters
})

const modalFlyoutFilters = computed(() => {
  const filters = [{ value: 'timeframe', label: 'rc_performance_timeframe' }]
  if (props.selectedArea === 'overview') {
    filters.push({ value: 'aggregation', label: 'rc_performance_group_data' })
  }
  if (props.selectedArea === 'overview' && props.selectedDisplayType === 'graph') {
    filters.push({ value: 'category', label: 'rc_performance_category' })
  }
  if (props.selectedArea === 'bookings') {
    filters.push({ value: 'status', label: 'rc_booking_status_header' })
  }
  filters.push({ value: 'markets', label: 'rc_performance_markets' })
  if (props.selectedArea === 'bookings') {
    filters.push({ value: 'columns', label: 'rc_column_picker' })
  }

  return filters
})

const filtersFlayoutOpened = ref(false)
const mobileFiltersOpened = ref(false)
const bookingFiltersOpened = ref(false)
const {
  selectedPeriod,
  selectedBookingPeriod,
  selectedAggregation,
  selectedGraph,
  selectedDateType,
  selectedMarket,
  selectedStatus,
  appliedFiltersAmount,
  bookingSortColumn,
} = useRateConnectPerformance()

const openMobileFilters = () => {
  mobileFiltersOpened.value = true
}
const closeMobileFilters = () => {
  mobileFiltersOpened.value = false
}

const openFlyoutsFilters = () => {
  filtersFlayoutOpened.value = true
}
const closeFlyoutsFilters = () => {
  filtersFlayoutOpened.value = false
}

const openBookingFilters = () => {
  bookingFiltersOpened.value = true
}
const closeBookingFilters = () => {
  bookingFiltersOpened.value = false
}

const statusOptions = computed(() => [
  { id: 0, name: 'rc_confirmed_booking_pay_per_stay', value: 'CONFIRMED' },
  { id: 1, name: 'rc_completed_stay_pay_per_stay', value: 'COMPLETED' },
  { id: 2, name: 'rc_canceled_booking_pay_per_stay', value: 'CANCELLED' },
])
const periodDatesOptions = computed(() => [
  {
    id: 0,
    name: 'trc_timeframe_last_14_days',
    value: 'last_14_days',
    label: t('trc_timeframe_last_14_days'),
    default: false,
  },
  {
    id: 1,
    value: 'current_month',
    name: 'trc_timeframe_this_month',
    label: t('trc_timeframe_this_month'),
    default: false,
  },
  { id: 2, value: 'last_30_days', name: 'last_30_days', label: t('last_30_days'), default: true },
  { id: 3, value: 'last_3_months', name: 'last_3_months', label: t('last_3_months'), default: false },
  { id: 4, value: 'last_6_months', name: 'last_6_months', label: t('last_6_months'), default: false },
  { id: 5, value: 'last_12_months', name: 'last_12_months', label: t('last_12_months'), default: false },
])

const periodDatesBookingOptions = computed(() => [
  {
    group: 'rc_booking_details_filter_days',
    options: [
      { value: 'last_30_days', label: 'rc_booking_filter_last_30_days' },
      { value: 'last_90_days', label: 'rc_booking_filter_last_90_days' },
      { value: 'next_30_days', label: 'rc_booking_filter_next_30_days' },
      { value: 'next_90_days', label: 'rc_booking_filter_next_90_days' },
    ],
  },
  {
    group: 'rc_booking_details_filter_months',
    options: [
      {
        value: 'current_month',
        label: 'rc_booking_filter_current_month',
        params: { Month: getMonthLabel('current', 0, 'current') },
      },
      {
        value: 'last_3_months',
        label: 'rc_booking_filter_last_3_months',
        params: { MonthStart: getMonthLabel('last', 3, 'start'), MonthEnd: getMonthLabel('last', 3, 'end') },
      },
      {
        value: 'last_1_month',
        label: 'rc_booking_filter_last_month',
        params: { Month: getMonthLabel('last', 1, 'start') },
      },
      {
        value: 'last_6_months',
        label: 'rc_booking_filter_last_6_months',
        params: { MonthStart: getMonthLabel('last', 6, 'start'), MonthEnd: getMonthLabel('last', 6, 'end') },
      },
      {
        value: 'last_12_months',
        label: 'rc_booking_filter_last_12_months',
        params: { MonthStart: getMonthLabel('last', 12, 'start'), MonthEnd: getMonthLabel('last', 12, 'end') },
      },
      {
        value: 'next_1_month',
        label: 'rc_booking_filter_next_month',
        params: { Month: getMonthLabel('next', 1, 'start') },
      },
      {
        value: 'next_3_months',
        label: 'rc_booking_filter_next_3_months',
        params: { MonthStart: getMonthLabel('next', 3, 'start'), MonthEnd: getMonthLabel('next', 3, 'end') },
      },
      {
        value: 'next_6_months',
        label: 'rc_booking_filter_next_6_months',
        params: { MonthStart: getMonthLabel('next', 6, 'start'), MonthEnd: getMonthLabel('next', 6, 'end') },
      },
    ],
  },
])
const periodDatesBookingMobileOptions = computed(() => periodDatesBookingOptions.value.flatMap(group => group.options))

const getMonth = (type, count = 0, position = 'current') => {
  const today = new Date()
  const currentMonth = today.getMonth() + 1

  let month

  if (type === 'current') {
    month = currentMonth
  } else if (type === 'last') {
    if (position === 'end') {
      month = currentMonth - 1
    } else if (position === 'start') {
      month = currentMonth - 1 - (count - 1)
    }
  } else if (type === 'next') {
    if (position === 'start') {
      month = currentMonth + 1
    } else if (position === 'end') {
      month = currentMonth + count
    }
  }

  while (month <= 0) month += 12
  while (month > 12) month -= 12

  return `month_${month}`
}

const getYear = (type, count = 0, position = 'current') => {
  const today = new Date()
  const currentMonth = today.getMonth() + 1
  const currentYear = today.getFullYear()

  let monthOffset = 0

  if (type === 'current') {
    monthOffset = 0
  } else if (type === 'last') {
    if (position === 'end') {
      monthOffset = -1
    } else if (position === 'start') {
      monthOffset = -count
    }
  } else if (type === 'next') {
    if (position === 'start') {
      monthOffset = 1
    } else if (position === 'end') {
      monthOffset = count
    }
  }

  const targetMonth = currentMonth + monthOffset
  const yearOffset = Math.floor((targetMonth - 1) / 12)

  return currentYear + yearOffset
}

const getMonthLabel = (type, count, position) => {
  const monthKey = getMonth(type, count, position)
  const year = getYear(type, count, position)
  const currentYear = new Date().getFullYear()

  if (year !== currentYear) {
    return `${t(monthKey)} ${year}`
  }
  return t(monthKey)
}

const aggregationOptions = computed(() => [
  {
    id: 0,
    name: 'rc_label_groupby',
    value: 'DAILY',
    label: t('rc_day'),
    default: true,
  },
  {
    id: 1,
    name: 'rc_label_groupby',
    value: 'WEEKLY',
    label: t('rc_week'),
    default: false,
  },
  {
    id: 2,
    value: 'MONTHLY',
    name: 'rc_label_groupby',
    label: t('rc_month'),
    default: false,
  },
])

const showGraphOptions = computed(() => {
  const statsOptions = [
    { id: 0, label: t('rc_bookings'), name: 'bookings', value: 'bookings' },
    { id: 1, label: t('kpi_chart_clicks'), name: 'clicks', value: 'clicks' },
    { id: 2, label: t('rc_revenue'), name: 'revenue', value: 'revenue' },
  ]
  if (props.overviewTableCampaignType === 'cpc') {
    statsOptions.push({ id: 3, label: t('rc_cpa'), name: 'cpa', value: 'cpa' })
  }
  return statsOptions
})

const dateTypes = computed(() => [
  { id: 0, value: 'bookingDate', name: 'bookingDate', label: t('rc_bookings_list_booking_date') },
  { id: 1, value: 'arrivalDate', name: 'arrivalDate', label: t('rc_booking_checkin') },
  { id: 2, value: 'departureDate', name: 'departureDate', label: t('rc_booking_checkout') },
])

const marketsArray = computed(() => {
  if (!props.markets || !Array.isArray(props.markets)) {
    return []
  }
  return [
    ...props.markets.map((market, index) => ({
      value: market.marketCode,
      label: t('locale_' + market.marketCode),
      id: index + 1,
    })),
  ].sort((a, b) => (a.label > b.label ? 1 : b.label > a.label ? -1 : 0))
})

const marketOptions = computed(() => {
  if (!Array.isArray(marketsArray.value)) return [{ value: 'all', name: t('rc_markets_all'), id: 0 }]
  return [{ value: 'all', label: t('rc_markets_all'), id: 0 }, ...marketsArray.value]
})

const applyFilters = tempFilters => {
  tempFilters.forEach(item => {
    onSelect(item)
  })
  closeMobileFilters()
  closeFlyoutsFilters()
  closeBookingFilters()
}

const onSelect = option => {
  if (option.name === 'markets') {
    onSelectedMarketChange(option)
  }
  if (option.name === 'bookingTimeframe') {
    onSelectedBookingPeriodChange(option)
  }
  if (option.name === 'timeframe') {
    onSelectedPeriodChange(option)
  }
  if (option.name === 'aggregation') {
    onSelectedAggregationChange(option)
  }
  if (option.name === 'category') {
    onSelectedGraphChange(option)
  }
  if (option.name === 'dateType') {
    onSelectedDateType(option)
  }
  if (option.name === 'status') {
    onSelectedStatus(option)
  }
}

const onSelectedMarketChange = e => {
  selectedMarket.value = e.option.value
}
const onSelectedPeriodChange = newPeriod => {
  selectedPeriod.value = newPeriod.option.value
}
const onSelectedBookingPeriodChange = newPeriod => {
  selectedBookingPeriod.value = newPeriod.option.value
  if (newPeriod.option.value.includes('next') && bookingSortColumn.value === 'bookingDate') {
    selectedDateType.value = 'departureDate'
    bookingSortColumn.value = 'departureDate'
  }
}
const onSelectedAggregationChange = aggregation => {
  selectedAggregation.value = aggregation.option.value
}
const onSelectedGraphChange = graph => {
  selectedGraph.value = graph.option.value
}
const onSelectedDateType = dateType => {
  selectedDateType.value = dateType.option.value
}
const onSelectedStatus = status => {
  const statusValue = status.option.map(item => item.value)
  selectedStatus.value = statusValue
}

const selectedPeriodValue = computed(() => periodDatesOptions.value.find(item => item.value === selectedPeriod.value))
const selectedPeriodBookingValue = computed(() => {
  return periodDatesBookingOptions.value
    .flatMap(group => group.options)
    .find(item => item.value === selectedBookingPeriod.value)
})

const selectedAggregationValue = computed(() =>
  aggregationOptions.value.find(item => item.value === selectedAggregation.value)
)
const selectedDateTypeValue = computed(() => dateTypes.value.find(item => item.value === selectedDateType.value))

const selectedShowGraphValue = computed(() => showGraphOptions.value.find(item => item.value === selectedGraph.value))
const selectedMarketValue = computed(() => marketOptions.value.find(item => item.value === selectedMarket.value))
const selectedStatusValue = computed(() => {
  return statusOptions.value.filter(item => selectedStatus.value.includes(item.value))
})
</script>
<style scoped>
button:hover .filter {
  stroke: var(--tw-color-white);
  fill: var(--tw-color-white);
}

button:hover .filter-badge {
  background-color: var(--tw-color-white);
  color: var(--tw-color-blue-700);
}
</style>
